import { RainGaugeLocation } from '../../../models/location-card.model';
import { AutocompleteField, SelectField, TextField } from '../../models/field-models';
import { LocationCardState } from '../../reducers';
import { findFieldById, findSectionById, normalizeSeriesName } from '../base-utils';
import { BasicInfoCommonFieldIds, TAssembledApiData } from '../common.models';
import {
    FORESITE_UL_MONITOR_CONFIGURATION_CONSTANTS,
    ForeSITEUL_CONSTANTS,
    ForeSiteULalarmsFieldIds,
    ForeSITEULDataDeliveryFieldIds,
    ForesiteULSectionIds,
    INSTALLATION_DEFAULT_CONSTANTS,
} from './base-config-models';
import { ChannelInstallationFieldIds, ElevationInstallationFieldIds } from './installation-types/config-models';
import { DefinedInstallationType, INSTALLATION_TYPE, InstallationType } from 'app/shared/models/location-details';
import { ForeSiteULLocationPayload, ForeSiteULConfigurationPayload } from './base-api-models';
import { LocationCommunicationType } from '../../../location-card.constants';
import { ForesiteULLocationCardState } from './models';
import { LocationStatus } from 'app/shared/models/locations';

export function gatherForeSiteULBasicInfoPayload(state: ForesiteULLocationCardState): ForeSiteULLocationPayload[] {
    let mp1Info: Partial<ForeSiteULLocationPayload> = { ...ForeSITEUL_CONSTANTS };
    Object.keys(SaveBasicInfoPayload).forEach((key) => {
        mp1Info[key] = SaveBasicInfoPayload[key](state);
    });

    const installationType = SaveInstallationPayload(state);
    mp1Info = { ...mp1Info, ...installationType };

    return [mp1Info] as ForeSiteULLocationPayload[];
}


export const SaveBasicInfoPayload: { [key: string]: (state: ForesiteULLocationCardState) => TAssembledApiData } = {
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName)!.value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, BasicInfoCommonFieldIds.AssignedRainGauge) as SelectField<
            RainGaugeLocation,
            string
        >;
        const rainGauge = rainGaugeField.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    communicationType: () => LocationCommunicationType.External,
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description)!.value,
    installationDate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.InstallationDate)!.value,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude)!.value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude)!.value,
    manholeaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeAddress)!.value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber)!.value,
    series: (state) => normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series)!.value as string),
    status: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Status)!.value,
    maintenanceInterval: (state) => {
        const status = findFieldById(state.formState, BasicInfoCommonFieldIds.Status).value;
        return status === LocationStatus.Maintenance
            ? findFieldById(state.formState, BasicInfoCommonFieldIds.MaintenanceInterval)?.value ?? null
            : null;
    },
    samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate)!.value,
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(
            state.formState,
            BasicInfoCommonFieldIds.CustomPropertiesSection,
        );
        const customProperties: { key: string; value: string }[] = [];

        let i = 0;
        while (true) {
            if (customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            const keyField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`
            ] as AutocompleteField<string, string>;

            const valueField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyValue}-${i}`
            ] as TextField;

            customProperties.push({
                key: keyField.value,
                value: valueField.value,
            });

            i++;
        }

        return customProperties;
    },
};

export const SaveInstallationPayload = (state: ForesiteULLocationCardState) => {
    const basicInfo = state.formState.config.sections.find((s) => s.id === ForesiteULSectionIds.BasicInfo);

    const installationTypeField = basicInfo?.fields[BasicInfoCommonFieldIds.InstallationType] as SelectField<
        InstallationType,
        string
    >;
    const installationType = installationTypeField.value;

    if (!installationType) {
        console.error('Installation type not found');
        return null;
    }

    const [type, shape] = installationType.split(':').map((s) => s.trim());

    if (type === INSTALLATION_TYPE.CHANNEL) {
        return {
            channel: {
                range: findFieldById(state.formState, ChannelInstallationFieldIds.Range)!.value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Channel,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Channel,
            installationtypeid: DefinedInstallationType.Channel,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.ELEVATION) {
        return {
            elevationInstallation: {
                datum: findFieldById(state.formState, ElevationInstallationFieldIds.Datum)!.value,
                physicalOffset: findFieldById(state.formState, ElevationInstallationFieldIds.PhysicalOffset)!.value,
                referencePoint: findFieldById(state.formState, ElevationInstallationFieldIds.ReferencePoint)!.value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Elevation,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Elevation,
            installationtypeid: DefinedInstallationType.Elevation,
            installationShapeType: shape,
        };
    }

    console.error('Installation type not supported');
    return null;
};

export function gatherForeSiteULconfigurationInfo(state: ForesiteULLocationCardState): ForeSiteULConfigurationPayload {
    const result: { [key: string]: (state: ForesiteULLocationCardState) => unknown } = {
        samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate)!.value,
        normalIntervalHours: (state) =>
            findFieldById(state.formState, ForeSITEULDataDeliveryFieldIds.NormalIntervalHours)!.value,
        fastModeEnabled: (state) =>
            findFieldById(state.formState, ForeSITEULDataDeliveryFieldIds.FastModeEnabled)!.value,
        lowLevelEnable: (state) => findFieldById(state.formState, ForeSiteULalarmsFieldIds.LowLevelEnable)!.value,
        lowLevelThreshold: (state) => findFieldById(state.formState, ForeSiteULalarmsFieldIds.LowLevelThreshold)!.value,
        highLevelEnable: (state) => findFieldById(state.formState, ForeSiteULalarmsFieldIds.HighLevelEnable)!.value,
        highLevelThreshold: (state) =>
            findFieldById(state.formState, ForeSiteULalarmsFieldIds.HighLevelThreshold)!.value,
        highHighEnable: (state) => findFieldById(state.formState, ForeSiteULalarmsFieldIds.HighHighEnable)!.value,
        highHighThreshold: (state) => findFieldById(state.formState, ForeSiteULalarmsFieldIds.HighHighThreshold)!.value,
        batteryLowEnable: (state) => findFieldById(state.formState, ForeSiteULalarmsFieldIds.BatteryLowEnable)!.value,
        batteryLowThreshold: (state) =>
            findFieldById(state.formState, ForeSiteULalarmsFieldIds.BatteryLowThreshold)!.value,
        serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber)!.value,
        useDaylightSavingTime: (state) => state.monitorConfiguration?.usedaylightsaving ?? false,
        storeTemperature: (state) => state.monitorConfiguration?.storetemperature ?? false,
        storeBattery: (state) => state.monitorConfiguration?.storebattery ?? 1,
        connectionType: () => LocationCommunicationType.External,
        syncTime: (state) => state.monitorConfiguration?.synctime ?? false,
        fastRate: (state) => state.monitorConfiguration?.fastrate ?? ForeSITEUL_CONSTANTS.fastrate,
        manholeDepth: (state) => state.monitorConfiguration?.manholedepth ?? 0,
    };

    return Object.keys(result).reduce(
        (acc, key) => {
            acc[key] = result[key](state);
            return acc;
        },
        { ...FORESITE_UL_MONITOR_CONFIGURATION_CONSTANTS },
    ) as ForeSiteULConfigurationPayload;
}
